import { useI18n } from "../../lib/i18n.jsx";
import { useReviews } from "../../lib/useReviews.js";
import { Star, Quote, ExternalLink } from "lucide-react";
import { useState } from "react";
import Reveal from "../Reveal.jsx";

function ReviewAvatar({ src, name }) {
  const [failed, setFailed] = useState(false);
  const initials = (name || "?")
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((p) => p[0])
    .join("")
    .toUpperCase();

  if (!src || failed) {
    return (
      <div
        data-testid="review-avatar-fallback"
        className="w-10 h-10 rounded-full bg-summer/15 text-summer font-display font-bold flex items-center justify-center text-sm shrink-0"
      >
        {initials}
      </div>
    );
  }
  return (
    <img
      src={src}
      alt={name || ""}
      onError={() => setFailed(true)}
      referrerPolicy="no-referrer"
      className="w-10 h-10 rounded-full object-cover shrink-0"
      data-testid="review-avatar"
    />
  );
}

export default function Reviews() {
  const { t, lang } = useI18n();
  const { loading, live, rating, ratingCount, mapsUri, reviews } = useReviews();

  // No live data and no fallback → don't render the section.
  if (!loading && !live) return null;

  const ratingValue = live ? Number(rating).toFixed(1) : "—";
  const items = (reviews || []).slice(0, 4);

  return (
    <section id="opinie" data-testid="reviews-section" className="bg-paper py-20 md:py-28">
      <div className="max-w-[1300px] mx-auto px-5 md:px-8">
        <Reveal as="div" className="text-center max-w-2xl mx-auto mb-12">
          <p className="font-handwritten text-summer text-2xl md:text-3xl mb-2">
            {t.reviews.kicker}
          </p>
          <h2
            className="font-display font-bold text-winter leading-tight mb-4"
            style={{ fontSize: "clamp(2rem, 4.5vw, 3.5rem)" }}
          >
            {t.reviews.title}
          </h2>
          {live && (
            <a
              href={mapsUri}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="reviews-google-link"
              className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-summer/10 hover:bg-summer/20 transition-colors"
            >
              <div className="flex">
                {[...Array(5)].map((_, i) => (
                  <Star key={i} className="w-4 h-4 fill-sun text-sun" />
                ))}
              </div>
              <span className="font-semibold text-winter text-sm">
                {ratingValue} · Google
                <span className="text-winter/60 font-normal">
                  {" "}({ratingCount} {{ pl: "opinii", uk: "відгуків", en: "reviews" }[lang] || "reviews"})
                </span>
              </span>
              <ExternalLink className="w-3.5 h-3.5 text-winter/60" />
            </a>
          )}
        </Reveal>

        {loading ? (
          <div className="grid md:grid-cols-2 gap-5" data-testid="reviews-loading">
            {[0, 1, 2, 3].map((i) => (
              <div key={i} className="card-soft p-7 md:p-8 animate-pulse">
                <div className="h-6 w-6 bg-winter/10 rounded mb-3" />
                <div className="h-4 bg-winter/10 rounded mb-2 w-full" />
                <div className="h-4 bg-winter/10 rounded mb-2 w-11/12" />
                <div className="h-4 bg-winter/10 rounded w-3/4" />
              </div>
            ))}
          </div>
        ) : (
          <div className="grid md:grid-cols-2 gap-5">
            {items.map((r, i) => (
              <Reveal
                key={i}
                variant="scale"
                delay={Math.min(i * 90, 360)}
                as="article"
                data-testid={`review-${i}`}
                className="card-soft p-7 md:p-8 flex flex-col"
              >
                <Quote className="w-8 h-8 text-summer/30 mb-3" />
                <p className="text-winter/85 leading-relaxed mb-5 line-clamp-6">
                  {r.text}
                </p>
                <div className="mt-auto flex items-center gap-3">
                  <ReviewAvatar src={r.author_photo_uri} name={r.author_name} />
                  <div className="min-w-0 flex-1">
                    <p className="font-display font-bold text-winter text-sm truncate">
                      {r.author_name}
                    </p>
                    {r.relative_time && (
                      <p className="text-xs text-winter/55 truncate">
                        {r.relative_time}
                      </p>
                    )}
                  </div>
                  <div className="flex shrink-0">
                    {[...Array(r.rating || 5)].map((_, j) => (
                      <Star key={j} className="w-3.5 h-3.5 fill-sun text-sun" />
                    ))}
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        )}

        {live && (
          <div className="text-center mt-10">
            <a
              href={mapsUri}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="reviews-see-all"
              className="inline-flex items-center gap-2 text-summer font-semibold hover:underline"
            >
              {{
                pl: "Zobacz wszystkie opinie na Google →",
                uk: "Дивитися всі відгуки в Google →",
                en: "See all reviews on Google →",
              }[lang] || "See all reviews on Google →"}
            </a>
          </div>
        )}
      </div>
    </section>
  );
}
